// Column definitions for the platform-wide Screener panel.
// Per Screener_and_Panel_Build_Spec.md §3: 11 columns total. Column 1
// (Entity / company name) is the frozen left pane; the other 10 live in
// the scrollable right pane and can be resized / reordered / hidden by
// the user, with state persisted to localStorage.
//
// Field names match ScreenerRow from apps/web/src/api/screener-client.ts.

import type { ScreenerRow } from '../api/screener-client.js';
import { EMPTY_VALUE, formatMoney as fmtMoney, formatCount as fmtCount } from '../format.js';
import {
  CURRENCY_SYMBOL,
  NATIVE_CURRENCY_BY_COUNTRY,
  NATIVE_CURRENCY_SYMBOL,
  convertCurrency,
  type GlobalCurrency,
} from '../currency.js';
import { evaluateScreenerRowQuality, isAtSizeClassCap } from './row-quality.js';

export const FROZEN_NAME_DEFAULT_WIDTH = 240;
export const COLUMN_MIN_WIDTH = 40;

// Right-pane column ids — match the field names on ScreenerRow.
// qualityDirection + yearsActive were REMOVED from the Screener on
// 2026-07-22 (Alex): trajectory rendered without its tier context (a
// "Stable" next to a "—" tier reads as data we don't have), and years
// active earns no scan-time decision. Persisted column state containing
// the retired ids is dropped by the config reconciler.
export const SCREENER_RIGHT_COLUMN_IDS = [
  'countryCode',
  'region',
  'subsegment',
  'turnoverScreener',
  'turnoverConfidenceP',
  'netAssets',
  'employees',
  'ownershipType',
  'tenure',
  'qualityTier',
  'originationSignal',
  'sellerReadiness',
] as const;
export type ScreenerRightColumnId = (typeof SCREENER_RIGHT_COLUMN_IDS)[number];

export interface ScreenerColumnDef {
  /** i18n key resolved at render time. */
  readonly labelKey: string;
  /** Initial / fallback width in pixels. */
  readonly defaultWidth: number;
  /** Right-align + monospace format for numeric columns. */
  readonly numeric: boolean;
  /** Server-side sort field name (matches the API's `sortBy` enum). */
  readonly sortField: string;
}

export const SCREENER_COLUMN_DEFS: Record<ScreenerRightColumnId, ScreenerColumnDef> = {
  countryCode: {
    labelKey: 'panels.screener.columns.country',
    defaultWidth: 56,
    numeric: false,
    sortField: 'country',
  },
  region: {
    labelKey: 'panels.screener.columns.region',
    defaultWidth: 130,
    numeric: false,
    sortField: 'region',
  },
  subsegment: {
    labelKey: 'panels.screener.columns.subsegment',
    defaultWidth: 180,
    numeric: false,
    sortField: 'subsegment',
  },
  turnoverScreener: {
    labelKey: 'panels.screener.columns.revenueEstimate',
    defaultWidth: 110,
    numeric: true,
    sortField: 'turnover_screener',
  },
  turnoverConfidenceP: {
    labelKey: 'panels.screener.columns.revenueConfidence',
    defaultWidth: 80,
    numeric: false,
    sortField: 'turnover_screener', // P-value not server-sortable; reuse revenue sort for now
  },
  netAssets: {
    labelKey: 'panels.screener.columns.netAssets',
    defaultWidth: 95,
    numeric: true,
    sortField: 'net_assets',
  },
  employees: {
    labelKey: 'panels.screener.columns.employees',
    defaultWidth: 80,
    numeric: true,
    sortField: 'employees',
  },
  ownershipType: {
    labelKey: 'panels.screener.columns.ownershipType',
    defaultWidth: 200,
    numeric: false,
    sortField: 'ownership_type',
  },
  tenure: {
    labelKey: 'panels.screener.columns.tenure',
    // "Owns freehold · 10 · leases 12" — the widest routine value.
    defaultWidth: 190,
    numeric: false,
    sortField: 'ownership_type', // not server-sortable
  },
  qualityTier: {
    labelKey: 'panels.screener.columns.qualityTier',
    defaultWidth: 100,
    numeric: false,
    sortField: 'quality_tier',
  },
  originationSignal: {
    labelKey: 'panels.screener.columns.originationSignal',
    defaultWidth: 112,
    numeric: true,
    sortField: 'origination_signal',
  },
  sellerReadiness: {
    labelKey: 'panels.screener.columns.sellerReadiness',
    defaultWidth: 118,
    numeric: true,
    sortField: 'seller_readiness',
  },
};

export interface ScreenerColumnState {
  id: ScreenerRightColumnId;
  width: number;
  visible: boolean;
}

export function defaultScreenerColumnState(): ScreenerColumnState[] {
  return SCREENER_RIGHT_COLUMN_IDS.map((id) => ({
    id,
    width: SCREENER_COLUMN_DEFS[id].defaultWidth,
    visible: true,
  }));
}

export function screenerGridTemplateColumns(columns: ScreenerColumnState[]): string {
  return columns
    .filter((c) => c.visible)
    .map((c) => `${c.width}px`)
    .join(' ');
}

export function screenerVisibleWidth(columns: ScreenerColumnState[]): number {
  return columns.filter((c) => c.visible).reduce((sum, c) => sum + c.width, 0);
}

// --- value formatters -------------------------------------------------

// Currency display uses the row's countryCode → native currency map
// (currency.ts — complete for all 27 markets). The platform-wide
// normalised metrics are denominated in the row's native currency
// (£ for UK, HUF for Hungary, € for the euro markets…). When a global
// display currency is supplied (the header selector), the native value
// is FX-rebased into it at display time (via EUR); otherwise it renders
// in the native currency, with its own symbol, unchanged.

// Thin country-aware wrappers around the workspace-wide format
// primitives. Money picks the right currency symbol per country code,
// then routes everything through ../format.ts so the rendering rules
// (2dp, K/M/B uppercase, bracket-negative) stay uniform across panels.

export function formatMoney(
  amount: number | null | undefined,
  countryCode: string,
  displayCurrency?: GlobalCurrency,
): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return EMPTY_VALUE;
  const native = NATIVE_CURRENCY_BY_COUNTRY[countryCode];
  // Rebase into the workspace display currency when both the selector
  // value and the row's native currency are known; fall back to the
  // native symbol (no conversion) otherwise. A market absent from the
  // native map must NEVER render bare — that hid the unconverted-HUF bug.
  if (displayCurrency && native) {
    return fmtMoney(convertCurrency(amount, native, displayCurrency), {
      currency: CURRENCY_SYMBOL[displayCurrency],
    });
  }
  return fmtMoney(amount, { currency: native ? NATIVE_CURRENCY_SYMBOL[native] : '' });
}

export function formatCount(n: number | null | undefined): string {
  return fmtCount(n);
}

// Cell render output — string or a richer descriptor that includes a
// suspect-flag (⚠) for modelled values whose inputs failed input-validation
// (see row-quality.ts), and a cap suffix when the model hit a size-class
// ceiling. The screener consumes this and renders accordingly.
export interface RenderedCell {
  display: string;
  suspect?: boolean;
  /** When set, the suspect flag's tooltip text describes which input(s)
   *  looked corrupt — surfaced as a title attribute on the cell. */
  suspectReason?: string;
  /** "(cap)" suffix marker — turnover sits exactly on a size-class ceiling. */
  atCap?: boolean;
}

// Per-row value renderer. Returns a RenderedCell so the panel can
// represent the suspect / cap markers visually rather than as plain text.
export function renderCell(
  row: ScreenerRow,
  columnId: ScreenerRightColumnId,
  displayCurrency?: GlobalCurrency,
): RenderedCell {
  switch (columnId) {
    case 'countryCode':
      return { display: row.countryCode ?? '—' };
    case 'region':
      return { display: row.region ?? '—' };
    case 'subsegment':
      return { display: row.subsegment ?? '—' };
    case 'turnoverScreener': {
      // Class estimate (BG solo-floor, 2026-07-16): a per-clinician SCOPE-GATE
      // figure for a solo practice (1 × €/clinician), NOT a filed or modelled
      // per-entity turnover. Condition 2 of the solo-floor ruling: never render
      // it as a precise value — qualify it as an estimate so a per-cell constant
      // (€60k across thousands of dentists) is never read as a real turnover.
      if (row.turnoverBasis === 'class_estimate') {
        return {
          display: `~${formatMoney(row.turnoverScreener, row.countryCode, displayCurrency)}`,
          suspect: true,
          suspectReason:
            'Class estimate — a per-clinician scope-gate figure for a solo practice ' +
            '(admits it to scope), not a filed or modelled turnover. Read it as a band, ' +
            'not a precise value; the ranking comes from Seller Readiness (owner age), not this figure.',
        };
      }
      // Show the modelled value AS-IS (don't suppress to "—"). The ⚠
      // marker on the cell + the company-overview banner alert the user
      // that inputs failed validation; hiding the figure would push the
      // row to the bottom of revenue-sorted views and effectively
      // screen it OUT — exactly the opposite of what flagging is for.
      // Rebase the native filed turnover into the display currency — the SAME
      // path every other panel (incl. Company Overview) uses, so the figure
      // agrees everywhere. (We deliberately do NOT swap to turnover_eur here:
      // it's ~1% off the native rebase, which made the screener disagree with
      // the Company Overview. The €150k floor is enforced on turnover_eur
      // server-side regardless, so a near-floor row may read ~€149k — accepted.)
      const display = formatMoney(row.turnoverScreener, row.countryCode, displayCurrency);
      const cap = isAtSizeClassCap(row.turnoverScreener, row.turnoverBasis);
      const persistedReasons = row.quarantineReasons ?? [];
      // TRUST THE SERVER. is_quarantined is computed at recompute with the
      // EUR-normalised value + the row's subsegment ceiling — inputs the
      // client doesn't have (the row carries native turnover only). The old
      // `server || client-re-eval` OR let the weaker client check override an
      // authoritative false: it compared native currency against a EUR
      // ceiling, so every HUF/RON/CZK/SEK row above ~€5M was falsely flagged
      // (invisible in GB, where GBP≈EUR). Client eval is the fallback only.
      const fallback = evaluateScreenerRowQuality(row);
      const quarantined = row.isQuarantined ?? fallback.quarantined;
      const reasons = persistedReasons.length > 0 ? persistedReasons : fallback.reasons;
      if (quarantined) {
        return {
          display,
          atCap: cap,
          suspect: true,
          suspectReason:
            'Modelled value suspect: inputs failed validation' +
            (reasons.length > 0 ? ` (${reasons.join(', ')})` : '') +
            '. The figure is shown so you can spot it, but treat it as unreliable.',
        };
      }
      return { display, atCap: cap };
    }
    case 'turnoverConfidenceP':
      return { display: row.turnoverConfidenceP ?? '—' };
    case 'netAssets':
      return { display: formatMoney(row.netAssets, row.countryCode, displayCurrency) };
    case 'employees':
      return { display: formatCount(row.employees) };
    case 'ownershipType':
      return { display: row.ownershipType ?? '—' };
    case 'tenure':
      return renderTenure(row);
    case 'qualityTier':
      // Treat the literal "Not rated" sentinel as missing data — render
      // the em dash instead so the column reads as quality data when
      // present and quiet when absent.
      return {
        display:
          !row.qualityTier || row.qualityTier === 'Not rated' ? EMPTY_VALUE : row.qualityTier,
      };
    case 'originationSignal':
      // Bare number when computed, em-dash when not — no confidence symbol on
      // the score (the confidence lives in its own column). 2026-07-16.
      return {
        display: row.originationSignal == null ? EMPTY_VALUE : `${row.originationSignal}`,
      };
    case 'sellerReadiness':
      // 0-100 absolute. Bare number when computed, em-dash when not — no
      // confidence symbol on the score.
      return {
        display: row.sellerReadiness == null ? EMPTY_VALUE : `${row.sellerReadiness}`,
      };
  }
}

// Premises tenure cell (migration 0202). Leads with the strongest evidence and the
// site count behind it; a mixed estate shows both sides ("Owns 3 · leases 2").
// Unknown renders as the em dash — never "leased": individuals' property is not
// published, so no owner found is not evidence of a landlord.
export function renderTenure(row: ScreenerRow): RenderedCell {
  const owned = row.tenureSitesOwned ?? 0;
  const landlord = row.tenureSitesLandlord ?? 0;
  switch (row.tenure) {
    case 'Owns premises': {
      const head = `${row.ownsFreehold ? 'Owns freehold' : 'Owns'} · ${owned}`;
      return { display: landlord > 0 ? `${head} · leases ${landlord}` : head };
    }
    case 'Related-party landlord':
      // An inference (propco / group company / the practitioner's SCI, matched by
      // shared address, group or name), not a registered fact — hence "Related".
      return { display: `Related landlord · ${landlord}` };
    case 'Third-party landlord':
      return { display: `Landlord · ${landlord}` };
    default:
      return { display: EMPTY_VALUE };
  }
}
